import type { IRBQMRepository } from "../repositories/RBQMRepository";
import type {
  SiteRisk,
  SiteRiskListParams,
  SiteRiskListResponse,
  KRIAlertListParams,
  KRIAlertListResponse,
  UpdateKRIAlertRequest,
  MonitoringQueueParams,
  MonitoringQueueResponse,
  RBQMDashboardData,
  CreateActionRequest,
  CreateActionResponse,
  SiteDetailData,
  KRIConfiguration,
  UpdateKRIConfigRequest,
} from "../types/rbqm.type";

// ─── Interface ───────────────────────────────────────────────────────────────

export interface IRBQMUseCase {
  // Dashboard
  getDashboard(studyId: string): Promise<RBQMDashboardData>;

  // Site Risk
  getSiteRisks(
    studyId: string,
    params?: SiteRiskListParams,
  ): Promise<SiteRiskListResponse>;
  getSiteRiskById(studyId: string, siteId: string): Promise<SiteRisk>;
  getSiteDetail(studyId: string, siteId: string): Promise<SiteDetailData>;

  // KRI Alerts
  getKRIAlerts(
    studyId: string,
    params?: KRIAlertListParams,
  ): Promise<KRIAlertListResponse>;
  updateKRIAlert(
    studyId: string,
    alertId: string,
    data: UpdateKRIAlertRequest,
  ): Promise<void>;

  // Monitoring Queue
  getMonitoringQueue(
    studyId: string,
    params?: MonitoringQueueParams,
  ): Promise<MonitoringQueueResponse>;

  // Actions
  createAction(
    studyId: string,
    data: CreateActionRequest,
  ): Promise<CreateActionResponse>;

  // KRI Configuration
  getKRIConfigurations(studyId: string): Promise<KRIConfiguration[]>;
  updateKRIConfiguration(
    studyId: string,
    configId: string,
    data: UpdateKRIConfigRequest,
  ): Promise<void>;
}

// ─── Implementation ──────────────────────────────────────────────────────────

export class RBQMUseCase implements IRBQMUseCase {
  constructor(private readonly rbqmRepository: IRBQMRepository) {}

  // ─── Dashboard ─────────────────────────────────────────────────────────────

  getDashboard(studyId: string): Promise<RBQMDashboardData> {
    return this.rbqmRepository.getDashboard(studyId);
  }

  // ─── Site Risk ─────────────────────────────────────────────────────────────

  getSiteRisks(
    studyId: string,
    params?: SiteRiskListParams,
  ): Promise<SiteRiskListResponse> {
    return this.rbqmRepository.getSiteRisks(studyId, params);
  }

  getSiteRiskById(studyId: string, siteId: string): Promise<SiteRisk> {
    return this.rbqmRepository.getSiteRiskById(studyId, siteId);
  }

  getSiteDetail(studyId: string, siteId: string): Promise<SiteDetailData> {
    return this.rbqmRepository.getSiteDetail(studyId, siteId);
  }

  // ─── KRI Alerts ────────────────────────────────────────────────────────────

  getKRIAlerts(
    studyId: string,
    params?: KRIAlertListParams,
  ): Promise<KRIAlertListResponse> {
    return this.rbqmRepository.getKRIAlerts(studyId, params);
  }

  updateKRIAlert(
    studyId: string,
    alertId: string,
    data: UpdateKRIAlertRequest,
  ): Promise<void> {
    return this.rbqmRepository.updateKRIAlert(studyId, alertId, data);
  }

  // ─── Monitoring Queue ──────────────────────────────────────────────────────

  getMonitoringQueue(
    studyId: string,
    params?: MonitoringQueueParams,
  ): Promise<MonitoringQueueResponse> {
    return this.rbqmRepository.getMonitoringQueue(studyId, params);
  }

  // ─── Actions ───────────────────────────────────────────────────────────────

  createAction(
    studyId: string,
    data: CreateActionRequest,
  ): Promise<CreateActionResponse> {
    return this.rbqmRepository.createAction(studyId, data);
  }

  // ─── KRI Configuration ─────────────────────────────────────────────────────

  getKRIConfigurations(studyId: string): Promise<KRIConfiguration[]> {
    return this.rbqmRepository.getKRIConfigurations(studyId);
  }

  updateKRIConfiguration(
    studyId: string,
    configId: string,
    data: UpdateKRIConfigRequest,
  ): Promise<void> {
    return this.rbqmRepository.updateKRIConfiguration(studyId, configId, data);
  }
}
